"use client";

import { useEffect, useRef, useState } from "react";
import Footer from "@/components/footer";
import LetsTalkForm from "@/components/lets-talk-form";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Types
type CastingTabKey =
  | "aluminium"
  | "steel"
  | "stainless"
  | "castiron"
  | "ductile"
  | "brass"
  | "bronze"
  | "copper";


const castingImages: Record<CastingTabKey, string> = {
  aluminium: "/casting/aluminium.jpeg",
  steel: "/casting/steel.jpg",
  stainless: "/casting/stainless.jpg",
  castiron: "/casting/cast-iron.jpg",
  ductile: "/casting/ductile.jpg",
  brass: "/casting/brass.jpg",
  bronze: "/casting/bronze.jpeg",
  copper: "/casting/copper.png",
};
type MouldingTabKey = "plastic" | "compression";

// Data
const castingTabs: {
  key: CastingTabKey;
  label: string;
  description: string;
}[] = [
  {
    key: "aluminium",
    label: "Aluminium Alloys",
    description:
      "Aluminium alloys are cast for applications requiring low weight, corrosion resistance, and good thermal conductivity. Alloy selection and casting parameters are defined by customer drawings and material specifications.",
  },
  {
    key: "steel",
    label: "Steel and Alloy Steel",
    description:
      "Steel and alloy steel castings are produced for structural and load-bearing components where strength and toughness are required. Grades and heat treatment conditions are controlled as per customer standards.",
  },
  {
    key: "stainless",
    label: "Stainless Steel",
    description:
      "Stainless steel castings are used where corrosion resistance and mechanical stability are required. Material grade and surface condition are defined by customer specifications.",
  },
  {
    key: "castiron",
    label: "Cast Iron",
    description:
      "Cast iron is processed for components requiring good machinability, damping characteristics, and compressive strength. Grade selection follows drawing and material requirements.",
  },
  {
    key: "ductile",
    label: "Ductile Cast Iron",
    description:
      "Ductile cast iron is used for applications requiring improved tensile strength and ductility compared to grey iron. Mechanical properties are controlled as specified.",
  },
  {
    key: "brass",
    label: "Brass",
    description:
      "Brass castings are produced for components requiring good machinability and corrosion resistance. Alloy composition is defined as per customer requirements.",
  },
  {
    key: "bronze",
    label: "Bronze",
    description:
      "Bronze is cast for wear-resistant and bearing applications. Material grades and properties are controlled per customer drawings.",
  },
  {
    key: "copper",
    label: "Copper & Copper Alloys",
    description:
      "Copper and copper alloy castings are processed for electrical and thermal applications as specified. Alloy selection and processing parameters are customer-defined.",
  },
];

const castingMethods = [
  {
    title: "Sand Casting",
    image: "/casting/sand.jpg",
  },
  {
    title: "Shell Casting",
    image: "/casting/shell.jpeg",
  },
  {
    title: "Die Casting",
    image: "/casting/die.jpeg",
  },
  {
    title: "Investment Casting",
    image: "/casting/investment.jpg",
  },
  {
    title: "Centrifugal Casting",
    image: "/casting/centri.jpeg",
  },
];

const mouldingTabs: {
  key: MouldingTabKey;
  label: string;
  description: string;
}[] = [
  {
    key: "plastic",
    label: "Plastic Injection Moulding",
    // description:
    //   "MMI provides expert plastic injection molding services designed to deliver precision, quality, and efficiency. With experience across industries like automotive, electronics and consumer goods, we manufacture durable, complex plastic parts tailored to your unique specifications.Our cutting-edge injection molding machines, combined with expert process control and mold engineering, ensure tight tolerances, excellent surface finishes, and minimal waste. We work with a wide range of thermoplastics and engineered polymers to meet the specific mechanical and aesthetic needs of industries.",
    description:
      "We offer high-precision plastic injection molding for mass production of detailed, consistent components.",
  },
  {
    key: "compression",
    label: "Compression Moulding",
    // description:
    //   "Compression moulding is ideal for producing large, thick-walled parts with excellent strength and durability. Commonly used for thermoset plastics, composites, and rubber components.",
    description:
      "Compression molding at MMI delivers durable thermoset parts ideal for strength and heat resistance",
  },
];

export default function CastingClient() {
  const [activeCasting, setActiveCasting] =
    useState<CastingTabKey>("aluminium");
  const [activeMoulding, setActiveMoulding] =
    useState<MouldingTabKey>("plastic");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const castingContent =
    castingTabs.find((t) => t.key === activeCasting) || castingTabs[0];
  const mouldingContent =
    mouldingTabs.find((t) => t.key === activeMoulding) || mouldingTabs[0];

  const tabsScrollRef = useRef<HTMLDivElement>(null);

  const scrollTabs = (direction: "left" | "right") => {
    if (!tabsScrollRef.current) return;

    tabsScrollRef.current.scrollBy({
      left: direction === "left" ? -300 : 300,
      behavior: "smooth",
    });
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* ====== HERO ====== */}
      <section className="py-8 md:py-12">
        <div className="mx-auto max-w-[1200px] px-4 md:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">
            {/* Left Text */}
            <div className="lg:w-[50%] flex flex-col justify-center space-y-4 md:space-y-6 text-center lg:text-left">
              <h1 className="text-2xl sm:text-3xl md:text-5xl text-[var(--mmi-heading)]">
                Casting
              </h1>

              <div className="flex justify-center lg:justify-start">
                <div className="w-24 md:w-32 h-1 bg-[var(--brand-yellow)]" />
              </div>
              <p className="text-sm sm:text-base md:text-lg text-[var(--mmi-body)] leading-relaxed">
                Casting programs are executed through approved suppliers in
                India, with tooling and machining support where required.
                Production follows customer drawings and specifications and
                covers a range of ferrous and non-ferrous alloys. Components can
                be supplied in prototype quantities or as ongoing scheduled
                batches, depending on project requirements and part complexity.
                We carry out casting operations based on customer drawings and
                specifications, supporting varied geometries, materials, and
                production volumes.
              </p>
            </div>
            {/* Right Placeholder Card (bigger on mobile) */}
            <div className="w-full lg:w-[35%] mx-auto lg:mx-0">
              <div className="relative w-full aspect-[4/3] rounded-[24px] overflow-hidden shadow-sm">
                <img
                  src="/casting.jpeg"
                  alt="Casting process"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== CASTING CAPABILITIES BAR ====== */}
      <section className="py-6 md:py-10">
        <div className="flex items-center justify-center px-4">
          <div className="w-full max-w-[920px] bg-[#FFF3D6] border border-[#FFD27A] rounded-[14px] overflow-hidden shadow-sm">
            <div
              className="grid grid-cols-[1fr_1.5fr_1fr] sm:grid-cols-3
                divide-x divide-[#FFD27A]"
            >
              <div
                className="flex items-center justify-center px-3 py-3
                  text-xs sm:text-base md:text-lg font-medium
                  text-[var(--mmi-heading)]
                  whitespace-normal sm:whitespace-nowrap
                  leading-tight text-center"
              >
                No MOQ
              </div>

              <div
                className="flex items-center justify-center px-3 py-3
                  text-xs sm:text-base md:text-lg font-medium
                  text-[var(--mmi-heading)]
                  whitespace-normal sm:whitespace-nowrap
                  leading-tight text-center"
              >
                Low-Volume to Mass Production
              </div>

              <div
                className="flex items-center justify-center px-3 py-3
                  text-xs sm:text-base md:text-lg font-medium
                  text-[var(--mmi-heading)]
                  whitespace-normal sm:whitespace-nowrap
                  leading-tight text-center"
              >
                Global Delivery
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== CASTING METHODS SUPPORTED ====== */}
      <section className="py-14 bg-white">
        <div className="max-w-[1300px] mx-auto px-6">
          {/* ===== Heading ===== */}
          <div className="text-center space-y-2 md:space-y-4 mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-5xl text-[var(--mmi-heading)]">
              Casting Methods Supported
            </h2>
            <div className="w-24 md:w-32 h-1 bg-[var(--brand-yellow)] mx-auto" />
          </div>

          {/* ===== Cards ===== */}
          {/* ===== Cards ===== */}
          <div className="grid grid-cols-3 md:grid-cols-5 gap-x-3 gap-y-10">
            {castingMethods.slice(0, 3).map((item) => (
              <div
                key={item.title}
                className="flex flex-col items-center gap-3"
              >
                <div className="relative w-[96px] h-[96px] sm:w-[120px] sm:h-[120px] md:w-[220px] md:h-[220px] overflow-hidden rounded-[18px] md:rounded-[28px]">
                  <Image
                    src={item.image}
                    alt={item.title}
                    fill
                    className="object-cover"
                  />
                </div>
                <p className="text-[13px] md:text-[16px] font-medium text-center">
                  {item.title}
                </p>
              </div>
            ))}

            {/* ===== Mobile-only centered second row ===== */}
            <div className="col-span-3 md:hidden flex justify-center gap-6">
              {castingMethods.slice(3).map((item) => (
                <div
                  key={item.title}
                  className="flex flex-col items-center gap-3"
                >
                  <div className="relative w-[96px] h-[96px] overflow-hidden rounded-[18px]">
                    <Image
                      src={item.image}
                      alt={item.title}
                      fill
                      className="object-cover"
                    />
                  </div>
                  <p className="text-[13px] font-medium text-center">
                    {item.title}
                  </p>
                </div>
              ))}
            </div>

            {/* ===== Desktop normal flow ===== */}
            {castingMethods.slice(3).map((item) => (
              <div
                key={item.title}
                className="hidden md:flex flex-col items-center gap-3"
              >
                <div className="relative w-[220px] h-[220px] overflow-hidden rounded-[28px]">
                  <Image
                    src={item.image}
                    alt={item.title}
                    fill
                    className="object-cover"
                  />
                </div>
                <p className="text-[16px] font-medium text-center">
                  {item.title}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== Casting Capabilities & Program Control ====== */}
      <section className="py-10 md:py-14 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          {/* Heading */}
          <div className="text-center mb-10 md:mb-14">
            <h2 className="text-[28px] sm:text-[34px] md:text-[48px] text-[var(--mmi-heading)] leading-tight">
              Casting Capabilities & Program Control
            </h2>
            <div className="mt-4 flex justify-center">
              <div className="w-24 md:w-32 h-[3px] bg-[var(--brand-yellow)] rounded-full" />
            </div>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
            {/* Card 1 */}
            <div className="rounded-[22px] border border-[#E5E5E5] bg-white p-6 md:p-8 shadow-sm">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-4 md:gap-5 text-center md:text-left">
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center md:shrink-0">
                  <Image
                    src="/icons/box.svg"
                    alt="Box"
                    width={60}
                    height={60}
                  />
                </div>

                <div>
                  <h3 className="text-[17px] md:text-[20px] font-semibold text-[var(--mmi-heading)] mb-2">
                    Tooling, Production Flexibility & Lead Times
                  </h3>
                  <p className="text-[14px] md:text-[16px] leading-[1.7] text-[var(--mmi-body)]">
                    In-house die and tooling development is supported through
                    our engineering team and qualified suppliers. Production
                    supports prototype, low-volume, and high-volume
                    requirements, with batch size and MOQ defined based on
                    customer needs and program scope.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 2 */}
            <div className="rounded-[22px] border border-[#E5E5E5] bg-white p-6 md:p-8 shadow-sm">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-4 md:gap-5 text-center md:text-left">
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center md:shrink-0">
                  <Image
                    src="/icons/house.svg"
                    alt="house"
                    width={60}
                    height={60}
                  />
                </div>

                <div>
                  <h3 className="text-[17px] md:text-[20px] font-semibold text-[var(--mmi-heading)] mb-2">
                    Manufacturing, Finishing & Assembly Scope
                  </h3>
                  <p className="text-[14px] md:text-[16px] leading-[1.7] text-[var(--mmi-body)]">
                    Scope includes casting, CNC machining, heat treatment, sand
                    and shot blasting, surface finishing, and assembly support.
                    Tool development for cast parts and sub-assembly with
                    testing support are provided as part of defined
                    manufacturing programs.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 3 */}
            <div className="rounded-[22px] border border-[#E5E5E5] bg-white p-6 md:p-8 shadow-sm">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-4 md:gap-5 text-center md:text-left">
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center md:shrink-0">
                  <Image
                    src="/icons/med.svg"
                    alt="Med"
                    width={60}
                    height={60}
                  />
                </div>

                <div>
                  <h3 className="text-[17px] md:text-[20px] font-semibold text-[var(--mmi-heading)] mb-2">
                    Process Control, Quality & Testing
                  </h3>
                  <p className="text-[14px] md:text-[16px] leading-[1.7] text-[var(--mmi-body)]">
                    Process control includes in-house spectrometer testing,
                    ultrasonic scanning, SPC-driven monitoring, and PPAP-based
                    validation. Quality and testing support includes simulation
                    and flow analysis, PT testing for porosity and shrinkage,
                    along with full material traceability and documentation.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 4 */}
            <div className="rounded-[22px] border border-[#E5E5E5] bg-white p-6 md:p-8 shadow-sm">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-4 md:gap-5 text-center md:text-left">
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center md:shrink-0">
                  <Image
                    src="/icons/truck.svg"
                    alt="Truck"
                    width={60}
                    height={60}
                  />
                </div>

                <div>
                  <h3 className="text-[17px] md:text-[20px] font-semibold text-[var(--mmi-heading)] mb-2">
                    Logistics & Program Support
                  </h3>
                  <p className="text-[14px] md:text-[16px] leading-[1.7] text-[var(--mmi-body)]">
                    Support covers material sourcing, casting, machining,
                    finishing, assembly, packaging, and shipment. Safety stock
                    is maintained at the factory, with warehousing near customer
                    locations to support global delivery, scalability, and risk
                    management.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#0E0E0E] text-white w-full overflow-x-hidden">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-14 md:py-24">
          <h2 className="text-[28px] sm:text-[34px] md:text-[48px] tracking-tight">
            Casting Services at MMI
          </h2>

          <div className="mt-4 h-[3px] w-24 bg-white rounded-full" />

          {/* Description */}
          <p className="mt-6 max-w-6xl text-[14px] sm:text-[16px] md:text-[18px] leading-[1.75] text-white/85">
            We support casting operations across a range of component geometries
            and material requirements, based on defined program needs. Our work
            follows approved processes and specified requirements to ensure
            proper fit, functional performance, and dimensional stability. The
            resulting cast components are aligned to their intended industrial
            use, supporting structural, mechanical, and precision applications
            referenced on this page.
          </p>
        </div>
      </section>

      <section className="pt-16 md:pt-24 pb-8 md:pb-10 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          {/* Heading */}
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-5xl text-[var(--mmi-heading)]">
              Why Choose Us for Casting?
            </h2>
            <div className="mt-4 flex justify-center">
              <div className="w-28 h-[3px] bg-[var(--brand-yellow)] rounded-full" />
            </div>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {/* Card 1 */}
            <div className="rounded-[28px] bg-[#FFF6E6] p-8 md:p-10 border border-[#FFD79A]">
              <h3 className="text-[16px] md:text-[18px] font-semibold text-[var(--mmi-heading)] mb-3">
                Pattern Development & Production Readiness
              </h3>
              <p className="text-[14px] md:text-[16px] leading-[1.7] text-[var(--mmi-body)]">
                We execute casting programs using customer-approved patterns and
                tooling. Pattern design, gating, riser placement, and draft
                angles are reviewed against part drawings prior to production to
                support manufacturability and dimensional stability.
              </p>
            </div>

            {/* Card 2 */}
            <div className="rounded-[28px] bg-[#FFF6E6] p-8 md:p-10 border border-[#FFD79A]">
              <h3 className="text-[16px] md:text-[18px] font-semibold text-[var(--mmi-heading)] mb-3">
                Casting & Secondary Operations
              </h3>
              <p className="text-[14px] md:text-[16px] leading-[1.7] text-[var(--mmi-body)]">
                Casting is carried out through approved external partners based
                on customer specifications. Secondary operations, including
                machining, shot blasting, heat treatment, and surface finishing,
                are coordinated as required by the part drawing and delivery
                condition.
              </p>
            </div>

            {/* Card 3 */}
            <div className="rounded-[28px] bg-[#FFF6E6] p-8 md:p-10 border border-[#FFD79A]">
              <h3 className="text-[16px] md:text-[18px] font-semibold text-[var(--mmi-heading)] mb-3">
                Supply Chain & Delivery
              </h3>
              <p className="text-[14px] md:text-[16px] leading-[1.7] text-[var(--mmi-body)]">
                Components are supplied as raw castings or finished parts based
                on customer requirements. We support domestic and international
                programs through coordinated logistics and partner execution,
                aligned with defined schedules and documentation.
              </p>
            </div>

            {/* Card 4 */}
            <div className="rounded-[28px] bg-[#FFF6E6] p-8 md:p-10 border border-[#FFD79A]">
              <h3 className="text-[16px] md:text-[18px] font-semibold text-[var(--mmi-heading)] mb-3">
                Materials & Alloy Control
              </h3>
              <p className="text-[14px] md:text-[16px] leading-[1.7] text-[var(--mmi-body)]">
                We produce cast components using aluminium alloys, carbon steel,
                stainless steel, cast iron, and other customer-specified
                materials. Chemical composition, mechanical properties, and
                material grades are controlled as defined in customer drawings,
                standards, or material specifications. Mill certificates and/or
                material test reports are always available.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ====== CASTING METHODS ====== */}
      <section className="py-8 bg-white overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12">
          {/* ===== Heading ===== */}
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-5xl text-[var(--mmi-heading)]">
              Casting Materials
            </h2>
            <div className="mt-3 w-24 md:w-32 h-1 bg-[var(--brand-yellow)] mx-auto" />
          </div>

          {/* ===== Tabs + Arrows ===== */}
          <div className="flex flex-col gap-3">
            {/* Arrows */}
            <div className="hidden md:flex justify-end gap-2">
              <button
                onClick={() => scrollTabs("left")}
                className="w-10 h-10 rounded-full border border-border bg-white flex items-center justify-center hover:bg-muted transition"
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <button
                onClick={() => scrollTabs("right")}
                className="w-10 h-10 rounded-full border border-border bg-white flex items-center justify-center hover:bg-muted transition"
                aria-label="Scroll right"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <nav
              role="tablist"
              aria-label="Casting Materials"
              ref={tabsScrollRef}
              className="w-full overflow-x-auto scrollbar-hide"
            >
              <div className="flex w-max gap-3 md:gap-6 px-1">
                {castingTabs.map((t) => {
                  const isActive = t.key === activeCasting;
                  return (
                    <button
                      key={t.key}
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveCasting(t.key)}
                      className={[
                        "flex-shrink-0 h-12 sm:h-14 md:h-16 px-4 md:px-6",
                        "flex items-center justify-center text-sm sm:text-base md:text-lg font-medium",
                        "whitespace-nowrap transition border-b-2",
                        isActive
                          ? "tab-active-gradient text-foreground border-[var(--brand-yellow)]"
                          : "text-muted-foreground hover:text-foreground border-transparent",
                      ].join(" ")}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </nav>
          </div>

          {/* ===== Content ===== */}
          <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 items-start">
            {/* Left Text */}
            <div className="max-w-2xl">
              <h3 className="text-xl sm:text-2xl md:text-4xl text-[var(--mmi-heading)]">
                {castingContent.label}
              </h3>
              <p className="mt-4 text-sm sm:text-base md:text-lg text-[var(--mmi-body)] leading-relaxed">
                {castingContent.description}
              </p>
            </div>

            {/* Right Image */}
            <div className="flex justify-center md:justify-end">
              <div className="relative w-full md:w-[70%] max-w-[700px] aspect-[4/3] overflow-hidden rounded-2xl shadow-md">
                <Image
                  src={castingImages[activeCasting]}
                  alt={castingContent.label}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 700px"
                  priority
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== MOULDING SERVICES ====== */}
      {/* <section className="py-8">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12">
          <div className="text-center space-y-2 md:space-y-4 mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-[var(--mmi-heading)]">
              Moulding Services
            </h2>
            <div className="w-24 md:w-32 h-1 bg-[var(--brand-yellow)] mx-auto" />
          </div>

          <div className="mt-6 md:mt-12">
            <nav
              role="tablist"
              aria-label="Moulding Services"
              className="relative mx-auto w-full"
            >
              <div className="grid grid-cols-2">
                {mouldingTabs.map((t) => {
                  const isActive = t.key === activeMoulding;
                  return (
                    <button
                      key={t.key}
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveMoulding(t.key)}
                      className={[
                        "relative flex h-12 sm:h-14 md:h-20 items-center justify-center px-3 sm:px-4 text-sm sm:text-base md:text-xl font-medium rounded-t-2xl transition-colors border-b-2",
                        isActive
                          ? "tab-active-gradient text-foreground border-[var(--brand-yellow)]"
                          : "text-muted-foreground hover:text-foreground border-transparent",
                      ].join(" ")}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </nav>
          </div>

          <div className="mt-8 md:mt-14 grid grid-cols-1 md:grid-cols-2 items-start gap-6 md:gap-10">
            <div className="order-1 max-w-2xl">
              <h3 className="text-xl sm:text-2xl md:text-4xl font-semibold text-[var(--mmi-heading)]">
                {mouldingContent.label}
              </h3>
              <p className="mt-3 md:mt-5 text-sm sm:text-base md:text-lg text-[var(--mmi-body)] leading-relaxed whitespace-pre-line">
                {mouldingContent.description}
              </p>
            </div>
            <div className="order-2 flex justify-center md:justify-end">
              <div className="w-full md:w-[70%] max-w-[700px] h-[56vh] md:h-auto md:aspect-[4/3] rounded-2xl bg-[#d9d9d9] shadow-md" />
            </div>
          </div>
        </div>
      </section> */}

      {/* ====== SIZE & WEIGHT ====== */}
      {/* <section className="py-8 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12">
          <div className="text-center space-y-2 md:space-y-4 mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-[var(--mmi-heading)]">
              Size & Weight Capacity
            </h2>
            <div className="w-24 md:w-32 h-1 bg-[var(--brand-yellow)] mx-auto" />
          </div>

          <div className="flex items-center justify-center">
            <div className="w-full max-w-[680px] bg-gray-100 rounded-2xl overflow-hidden">
              <div className="grid grid-cols-2 divide-x divide-gray-300">
                <div className="px-5 py-3 text-center text-sm sm:text-base md:text-lg text-[var(--mmi-heading)] font-medium">
                  Cast Part Range: 0.05 kg to 500 kg
                </div>
                <div className="px-5 py-3 text-center text-sm sm:text-base md:text-lg text-[var(--mmi-heading)] font-medium">
                  Maximum Casting Weight: 500 kg
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* ====== MATERIALS HANDLED ====== */}
      {/* <section className="py-8">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12">
          <div className="text-center space-y-2 md:space-y-4 mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-[var(--mmi-heading)]">
              Materials Handled
            </h2>
            <div className="w-24 md:w-32 h-1 bg-[var(--brand-yellow)] mx-auto" />
          </div>

          <div className="flex items-center justify-center">
            <div className="w-full max-w-[680px] bg-gray-100 rounded-2xl overflow-hidden">
              <div className="grid grid-cols-2 divide-x divide-gray-300">
                <div className="px-5 py-3 text-center text-sm sm:text-base md:text-lg text-[var(--mmi-heading)] font-medium">
                  All Ferrous & Non-Ferrous Alloys
                </div>
                <div className="px-5 py-3 text-center text-sm sm:text-base md:text-lg text-[var(--mmi-heading)] font-medium">
                  Custom Shapes and Compositions Supported
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* ====== PATTERN & MOULD MAKING ====== */}
      {/* <section className="py-8 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12">
          <div className="text-center space-y-2 md:space-y-4 mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-[var(--mmi-heading)]">
              Pattern & Mould Making
            </h2>
            <div className="w-24 md:w-32 h-1 bg-[var(--brand-yellow)] mx-auto" />
          </div>

          <div className="flex items-center justify-center">
            <div className="w-full max-w-[680px] bg-gray-100 rounded-2xl overflow-hidden">
              <div className="px-5 py-3 text-center text-sm sm:text-base md:text-lg text-[var(--mmi-heading)] font-medium">
                In-House Pattern and Mould Development
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* ====== TOLERANCES ====== */}
      {/* <section className="py-8">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12">
          <div className="text-center space-y-2 md:space-y-4 mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-[var(--mmi-heading)]">
              Tolerances
            </h2>
            <div className="w-24 md:w-32 h-1 bg-[var(--brand-yellow)] mx-auto" />
          </div>

          <div className="flex items-center justify-center">
            <div className="w-full max-w-[680px] bg-gray-100 rounded-2xl overflow-hidden">
              <div className="grid grid-cols-2 divide-x divide-gray-300">
                <div className="px-5 py-3 text-center text-sm sm:text-base md:text-lg text-[var(--mmi-heading)] font-medium">
                  Per Customer Specification
                </div>
                <div className="px-5 py-3 text-center text-sm sm:text-base md:text-lg text-[var(--mmi-heading)] font-medium">
                  Investment Casting: ±0.5 mm
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* ====== FEATURES GRID (text first on mobile, alternate on desktop) ====== */}
      {/* <section className="py-10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-12 md:gap-y-20 gap-x-6 md:gap-x-12 items-start">
            <div className="order-1 md:order-1 space-y-4 md:space-y-6">
              <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-[var(--mmi-heading)]">
                Post-Casting Operations
              </h2>
              <ul className="space-y-2 md:space-y-3 text-sm sm:text-base md:text-lg text-[var(--mmi-heading)] font-semibold">
                {[
                  "In-House Machining",
                  "Heat Treatment",
                  "Sand/Shot Blasting",
                  "Surface Finishing",
                ].map((b, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 leading-relaxed"
                  >
                    <span className="text-[var(--brand-yellow)] mt-1.5">•</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="order-2 md:order-2 flex md:justify-start">
              <div className="w-full md:w-[80%] max-w-[520px] h-[56vh] md:h-auto md:aspect-[4/3] rounded-2xl bg-[#d9d9d9] shadow-md" />
            </div>

            <div className="order-4 md:order-3 flex md:justify-start">
              <div className="w-full md:w-[80%] max-w-[520px] h-[56vh] md:h-auto md:aspect-[4/3] rounded-2xl bg-[#d9d9d9] shadow-md" />
            </div>
            <div className="order-3 md:order-4 space-y-4 md:space-y-6">
              <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-[var(--mmi-heading)]">
                Volume Flexibility
              </h2>
              <ul className="space-y-2 md:space-y-3 text-sm sm:text-base md:text-lg text-[var(--mmi-heading)] font-semibold">
                {[
                  "Low-Volume and Mass Production",
                  "Prototypes Supported (With Volume Commitment)",
                ].map((b, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 leading-relaxed"
                  >
                    <span className="text-[var(--brand-yellow)] mt-1.5">•</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="order-5 md:order-5 space-y-4 md:space-y-6">
              <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-[var(--mmi-heading)]">
                Quality & Testing
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-[var(--mmi-body)] leading-relaxed">
                Our comprehensive quality assurance processes ensure every
                casting meets the highest standards of precision and
                reliability.
              </p>
              <ul className="space-y-2 md:space-y-3 text-sm sm:text-base md:text-lg text-[var(--mmi-heading)] font-semibold">
                {[
                  "Simulation and Flow Analysis",
                  "PT Testing for Porosity and Shrinkage",
                  "Material Traceability and Documentation",
                ].map((b, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 leading-relaxed"
                  >
                    <span className="text-[var(--brand-yellow)] mt-1.5">•</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="order-6 md:order-6 flex md:justify-start">
              <div className="w-full md:w-[80%] max-w-[520px] h-[56vh] md:h-auto md:aspect-[4/3] rounded-2xl bg-[#d9d9d9] shadow-md" />
            </div>

            <div className="order-8 md:order-7 flex md:justify-start">
              <div className="w-full md:w-[80%] max-w-[520px] h-[56vh] md:h-auto md:aspect-[4/3] rounded-2xl bg-[#d9d9d9] shadow-md" />
            </div>
            <div className="order-7 md:order-8 space-y-4 md:space-y-6">
              <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-[var(--mmi-heading)]">
                Additional Capabilities
              </h2>
              <ul className="space-y-2 md:space-y-3 text-sm sm:text-base md:text-lg text-[var(--mmi-heading)] font-semibold">
                {[
                  "Tool Development for Cast and Machined Parts",
                  "Sub-Assemblies and Testing Support",
                ].map((b, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 leading-relaxed"
                  >
                    <span className="text-[var(--brand-yellow)] mt-1.5">•</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section> */}

      {/* ====== LET'S TALK ====== */}
      <section className="py-8 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-start">
            <div className="space-y-2 md:space-y-4">
              <h2 className="text-2xl sm:text-3xl md:text-5xl text-[var(--mmi-heading)]">
                Submit An Inquiry
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-[var(--mmi-body)] leading-relaxed">
                Need a quote, feedback, or technical guidance? We're here to
                help!
              </p>
            </div>
            <LetsTalkForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
